import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { API, authHeader } from "../../lib/adminApi";
import { useContent } from "../../context/ContentContext";

export default function SettingsTab() {
  const { content, refresh } = useContent();
  const [s, setS] = useState(() => JSON.parse(JSON.stringify(content.settings)));
  const [saving, setSaving] = useState(false);

  const set = (path, value) => {
    setS((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = Array.isArray(obj[keys[i]]) ? [...obj[keys[i]]] : { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, s, authHeader());
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Session expired \u2014 log in again." : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const input =
    "w-full bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-2.5 text-[#1A1A1A]";
  const Label = ({ children }) => (
    <label className="block text-[11px] tracking-[0.16em] uppercase text-[#8A857A] mb-1.5">{children}</label>
  );
  const Card = ({ title, children }) => (
    <div className="bg-white border border-[#E4DBCB] p-6 mb-6">
      <h2 className="font-serif-display text-xl text-[#1A1A1A] mb-5">{title}</h2>
      {children}
    </div>
  );

  // hours helpers
  const setHour = (i, key, val) => {
    const hours = [...(s.hours || [])];
    hours[i] = { ...hours[i], [key]: val };
    set("hours", hours);
  };
  const addHour = () => set("hours", [...(s.hours || []), { day: "", time: "" }]);
  const removeHour = (i) => set("hours", (s.hours || []).filter((_, x) => x !== i));

  // highlights helpers
  const setHl = (i, key, val) => {
    const hl = [...(s.about.highlights || [])];
    hl[i] = { ...hl[i], [key]: val };
    set("about.highlights", hl);
  };
  const addHl = () => set("about.highlights", [...(s.about.highlights || []), { value: "", label: "" }]);
  const removeHl = (i) => set("about.highlights", (s.about.highlights || []).filter((_, x) => x !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-3xl text-[#1A1A1A]">Wording, Hours &amp; Contact</h1>
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#0C0C0C] text-[#C6A15B] text-xs tracking-[0.2em] uppercase px-7 py-3 hover:bg-[#1a1a1a] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save All"}
        </button>
      </div>

      <Card title="Homepage Hero">
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <Label>{`Title line ${i + 1}`}</Label>
              <input
                value={(s.hero.titleLines || [])[i] || ""}
                onChange={(e) => {
                  const t = [...(s.hero.titleLines || ["", "", ""])];
                  t[i] = e.target.value;
                  set("hero.titleLines", t);
                }}
                className={input}
              />
            </div>
          ))}
        </div>
        <div className="mb-4">
          <Label>Eyebrow (small text above)</Label>
          <input value={s.hero.eyebrow || ""} onChange={(e) => set("hero.eyebrow", e.target.value)} className={input} />
        </div>
        <div>
          <Label>Subtitle</Label>
          <textarea rows={2} value={s.hero.subtitle || ""} onChange={(e) => set("hero.subtitle", e.target.value)} className={input + " resize-none"} />
        </div>
      </Card>

      <Card title="About Section">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label>Eyebrow</Label>
            <input value={s.about.eyebrow || ""} onChange={(e) => set("about.eyebrow", e.target.value)} className={input} />
          </div>
          <div>
            <Label>Heading</Label>
            <input value={s.about.heading || ""} onChange={(e) => set("about.heading", e.target.value)} className={input} />
          </div>
        </div>
        <div className="mb-6">
          <Label>Body</Label>
          <textarea rows={4} value={s.about.body || ""} onChange={(e) => set("about.body", e.target.value)} className={input + " resize-none"} />
        </div>
        <div className="flex items-center justify-between mb-3">
          <Label>Highlights (3 stat boxes)</Label>
          <button onClick={addHl} className="flex items-center gap-1 text-xs text-[#C6A15B]"><Plus size={14} /> Add</button>
        </div>
        {(s.about.highlights || []).map((h, i) => (
          <div key={i} className="flex gap-3 mb-3">
            <input value={h.value} onChange={(e) => setHl(i, "value", e.target.value)} placeholder="Value" className={input} />
            <input value={h.label} onChange={(e) => setHl(i, "label", e.target.value)} placeholder="Label" className={input} />
            <button onClick={() => removeHl(i)} className="text-[#b04a3f] shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </Card>

      <Card title="Contact">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Phone</Label>
            <input value={s.contact.phone || ""} onChange={(e) => set("contact.phone", e.target.value)} className={input} />
          </div>
          <div>
            <Label>Email</Label>
            <input value={s.contact.email || ""} onChange={(e) => set("contact.email", e.target.value)} className={input} />
          </div>
        </div>
      </Card>

      <Card title="Business Hours">
        {(s.hours || []).map((h, i) => (
          <div key={i} className="flex gap-3 mb-3">
            <input value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} placeholder="Day(s)" className={input} />
            <input value={h.time} onChange={(e) => setHour(i, "time", e.target.value)} placeholder="Hours" className={input} />
            <button onClick={() => removeHour(i)} className="text-[#b04a3f] shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={addHour} className="flex items-center gap-1 text-xs text-[#C6A15B] mt-1"><Plus size={14} /> Add row</button>
      </Card>

      <Card title="Links & Branding">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Tagline</Label>
            <input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={input} />
          </div>
          <div>
            <Label>Instagram URL</Label>
            <input value={s.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={input} />
          </div>
          <div>
            <Label>Booking URL</Label>
            <input value={s.bookUrl || ""} onChange={(e) => set("bookUrl", e.target.value)} className={input} />
          </div>
          <div>
            <Label>Services URL</Label>
            <input value={s.servicesUrl || ""} onChange={(e) => set("servicesUrl", e.target.value)} className={input} />
          </div>
        </div>
      </Card>
    </div>
  );
}
